import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, Gear, Warning, ListChecks, Truck, ArrowsClockwise } from '@phosphor-icons/react';
import { PlanningDayDialog } from '../../components/admin/planning/PlanningDayDialog';
import { AssignConfirmDialog } from '../../components/admin/planning/AssignConfirmDialog';
import { PlanningSettingsDialog } from '../../components/admin/planning/PlanningSettingsDialog';
import { PendingAllocationsDialog } from '../../components/admin/planning/PendingAllocationsDialog';
import { fmtD } from '../../components/admin/planning/planningUtils';

function monthRange(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(last) };
}

const CARDS = [
  ['today_capacity', "Today's Capacity"], ['today_confirmed', 'Confirmed Today'],
  ['today_tentative', 'Tentative Today'], ['today_available', 'Available Today'],
  ['pending_assignment', 'Pending Assignment'], ['pending_procurement', 'Pending Procurement'],
  ['delivery_risk', 'Delivery Risk'], ['overdue_production', 'Overdue Production'],
  ['production_due_today', 'Production Due Today'], ['delivery_due_today', 'Delivery Due Today'],
];

export default function ProductionPlanning() {
  const [anchor, setAnchor] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [assignJo, setAssignJo] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [moveConflict, setMoveConflict] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = monthRange(anchor);
      const { data: d } = await api.get(`/api/admin/production-planning/calendar?from_date=${from}&to_date=${to}`);
      setData(d);
    } catch (e) { toast.error('Failed to load production calendar'); }
    setLoading(false);
  }, [anchor]);

  useEffect(() => { load(); }, [load]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post('/api/admin/production-planning/refresh');
      toast.success(`Re-allocated ${r.replanned} job orders${r.forced_qty > 0 ? ` (${r.forced_qty} units force-fitted to earliest dates)` : ''}`);
      await load();
    } catch (e) { toast.error('Refresh failed'); }
    setRefreshing(false);
  };

  useEffect(() => {
    const assignParam = searchParams.get('assign');
    if (assignParam) setAssignJo(assignParam);
  }, [searchParams]);

  useEffect(() => {
    // pending popup once per session on calendar open
    if (data && !sessionStorage.getItem('pp_pending_shown')) {
      const hasPending = (data.cards?.pending_assignment || 0) > 0 || (data.cards?.delivery_risk || 0) > 0;
      if (hasPending) {
        sessionStorage.setItem('pp_pending_shown', '1');
        setShowPending(true);
      }
    }
  }, [data]);

  const doMove = async (payload) => {
    try {
      await api.post('/api/admin/production-planning/move', payload);
      toast.success(`JO ${payload.jo_number} moved to ${fmtD(payload.to_date)}`);
      setMoveConflict(null); setOverrideReason('');
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.warnings) {
        setMoveConflict({ payload, warnings: det.warnings });
      } else {
        toast.error(typeof det === 'string' ? det : 'Move failed');
      }
    }
  };

  const onDrop = (ev, toDate) => {
    ev.preventDefault();
    try {
      const chip = JSON.parse(ev.dataTransfer.getData('text/plain'));
      if (chip.from_date === toDate) return;
      doMove({ jo_number: chip.jo_number, from_date: chip.from_date, to_date: toDate, qty: chip.qty });
    } catch { /* ignore */ }
  };

  const warning = data?.pending_procurement_warning;
  const days = data?.days || [];
  const monthLabel = anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const firstDow = days.length ? (new Date(days[0].date).getDay() + 6) % 7 : 0; // Mon=0

  return (
    <Layout>
      <div className="space-y-4" data-testid="production-planning-page">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-zinc-900">Production Planning Calendar</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={doRefresh} disabled={refreshing} data-testid="planning-refresh-btn"
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
              <ArrowsClockwise size={16} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Re-allocating…' : 'Refresh & Re-allocate'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPending(true)} data-testid="open-pending-btn">
              <ListChecks size={16} className="mr-1" /> Pending Allocations
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/delivery-calendar')} data-testid="open-delivery-calendar-btn">
              <Truck size={16} className="mr-1" /> Delivery Calendar
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="open-planning-settings-btn">
              <Gear size={16} className="mr-1" /> Capacity & Settings
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        {data?.cards && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="planning-summary-cards">
            {CARDS.map(([key, label]) => (
              <button key={key} onClick={() => (key === 'pending_assignment' || key === 'delivery_risk' || key === 'pending_procurement') ? setShowPending(true) : null}
                className={`p-2.5 rounded-sm border text-left transition-colors hover:ring-2 hover:ring-blue-200 ${
                  key === 'delivery_risk' && data.cards[key] > 0 ? 'border-red-300 bg-red-50' :
                  key === 'pending_procurement' && data.cards[key] > 0 ? 'border-orange-300 bg-orange-50' :
                  'border-zinc-200 bg-white'}`}
                data-testid={`card-${key}`}>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
                <div className="text-xl font-bold">{data.cards[key]}</div>
              </button>
            ))}
          </div>
        )}

        {/* Pending procurement red warning */}
        {warning && warning.pending_procurement_count > 0 && (
          <div className="p-3 rounded-sm border-2 border-red-500 bg-red-50" data-testid="pending-procurement-warning">
            <div className="flex items-start gap-3">
              <Warning size={28} className="text-red-600 flex-shrink-0" weight="fill" />
              <div className="flex-1">
                <div className="text-red-700 font-bold text-sm uppercase tracking-wide">URGENT: Pending Procurement Approval</div>
                <div className="text-xs text-red-800 mt-0.5">
                  Some job orders with upcoming delivery dates are awaiting procurement approval. Production capacity has been
                  tentatively reserved. Approve procurement immediately to avoid delivery delay.
                </div>
                <div className="flex flex-wrap gap-4 mt-2 text-xs font-bold text-red-900">
                  <span>Pending JOs: {warning.pending_procurement_count}</span>
                  <span>Pending Qty: {warning.pending_qty}</span>
                  <span>Earliest Delivery: {fmtD(warning.earliest_delivery)}</span>
                  <span>At Delivery Risk: {warning.at_risk_count}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700" onClick={() => setShowPending(true)} data-testid="warning-view-list-btn">View Pending List</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs border-red-400 text-red-700" onClick={() => navigate('/job-orders')}>Procurement Approval</Button>
              </div>
            </div>
          </div>
        )}

        {/* Month navigation */}
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))} data-testid="prev-month-btn">
            <CaretLeft size={16} />
          </Button>
          <div className="text-lg font-bold">{monthLabel}</div>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))} data-testid="next-month-btn">
            <CaretRight size={16} />
          </Button>
        </div>

        {/* Calendar grid */}
        {loading ? <div className="p-10 text-center text-zinc-400">Loading calendar…</div> : (
          <div className="grid grid-cols-7 gap-1" data-testid="planning-calendar-grid">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-wider text-zinc-500 text-center font-bold py-1">{d}</div>
            ))}
            {Array.from({ length: firstDow }).map((_, i) => <div key={`pad-${i}`} />)}
            {days.map((d) => {
              const pendingLoad = Math.max(0, (d.confirmed + d.tentative) - (d.completed || 0));
              const pct = d.capacity > 0 ? (pendingLoad / d.capacity) * 100 : 0;
              const bg = d.is_non_working ? 'bg-zinc-100 opacity-60' :
                d.capacity_exceeded ? 'bg-red-100 border-red-400' :
                pct > 90 ? 'bg-red-50 border-red-200' :
                pct > 60 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
              return (
                <div key={d.date}
                  className={`min-h-[92px] p-1 rounded-sm border cursor-pointer hover:ring-2 hover:ring-blue-300 ${bg}`}
                  onClick={() => setSelectedDay(d)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onDrop(e, d.date)}
                  data-testid={`plan-day-${d.date}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-bold">{d.date.slice(-2)}</span>
                    <span className="flex items-center gap-1">
                      {d.overbooked && (
                        <span className="text-[8px] font-bold uppercase px-1 py-0.5 rounded bg-rose-600 text-white leading-none"
                          title={`Overbooked — ${d.forced_qty > 0 ? `${d.forced_qty} units force-fitted. ` : ''}Extra manpower needed`}
                          data-testid={`overbooked-badge-${d.date}`}>
                          ⚠ OVER{d.forced_qty > 0 ? ` +${d.forced_qty}` : ''}
                        </span>
                      )}
                      {d.holiday && <span className="text-[8px] uppercase font-bold text-red-600">{d.holiday.kind}</span>}
                    </span>
                  </div>
                  {!d.is_non_working && (
                    <div className="text-[9px] font-mono text-zinc-600">
                      C:{d.confirmed} T:{d.tentative} / {d.capacity} <span className="text-emerald-700 font-bold">A:{d.available}</span>
                    </div>
                  )}
                  <div className="mt-0.5 space-y-0.5 max-h-[52px] overflow-hidden">
                    {d.jos.slice(0, 3).map((jo) => (
                      <div key={jo.jo_number} draggable={!jo.is_completed}
                        onDragStart={(e) => {
                          e.stopPropagation();
                          e.dataTransfer.setData('text/plain', JSON.stringify({ jo_number: jo.jo_number, from_date: d.date, qty: jo.day_qty }));
                        }}
                        onClick={(e) => { e.stopPropagation(); setSelectedDay(d); }}
                        title={`${jo.customer_name} — ${jo.allocation_label}`}
                        className={`text-[9px] px-1 py-0.5 rounded truncate font-mono cursor-grab active:cursor-grabbing ${
                          jo.is_completed ? 'bg-emerald-200 text-emerald-900 border border-emerald-400' :
                          jo.forced ? 'bg-rose-200 text-rose-900 border border-rose-500' :
                          jo.is_tentative ? 'bg-orange-200 text-orange-900 border border-orange-400' :
                          jo.risk === 'high' || jo.risk === 'overdue' ? 'bg-red-200 text-red-900' :
                          'bg-blue-100 text-blue-800'}`}>
                        {jo.jo_number} ×{jo.day_qty}
                      </div>
                    ))}
                    {d.jos.length > 3 && <div className="text-[9px] text-zinc-500 pl-1">+{d.jos.length - 3} more…</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-3 text-[10px] text-zinc-500">
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-blue-100 border border-blue-300 mr-1" />Confirmed</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-orange-200 border border-orange-400 mr-1" />Tentative (Pending Procurement)</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-red-200 mr-1" />Delivery Risk</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-emerald-200 border border-emerald-400 mr-1" />Completed / Dispatched / Installed</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded bg-rose-200 border border-rose-500 mr-1" />Force-fitted (Overbooked — extra manpower needed)</span>
          <span>Drag a JO chip onto another date to reschedule.</span>
        </div>
      </div>

      <PlanningDayDialog day={selectedDay} onOpenChange={setSelectedDay}
        onAssign={(jn) => { setSelectedDay(null); setAssignJo(jn); }} />
      <AssignConfirmDialog joNumber={assignJo} open={!!assignJo}
        onOpenChange={(o) => !o && setAssignJo(null)} onDone={load} />
      <PlanningSettingsDialog open={showSettings} onOpenChange={setShowSettings} onSaved={load} />
      <PendingAllocationsDialog open={showPending} onOpenChange={setShowPending}
        onAssign={(jn) => setAssignJo(jn)} />

      {/* Move override dialog */}
      <Dialog open={!!moveConflict} onOpenChange={(o) => { if (!o) { setMoveConflict(null); setOverrideReason(''); } }}>
        <DialogContent className="max-w-md bg-white" data-testid="move-override-dialog">
          <DialogHeader><DialogTitle className="text-red-700">Rescheduling Warning</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            {(moveConflict?.warnings || []).map((w, i) => (
              <div key={i} className="p-2 rounded-sm bg-red-50 border border-red-200 text-red-700 text-xs">{w}</div>
            ))}
            <div>
              <div className="text-xs text-zinc-600 mb-1">Override reason (mandatory, will be logged)</div>
              <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="move-override-reason" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setMoveConflict(null); setOverrideReason(''); }}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" disabled={!overrideReason.trim()}
                onClick={() => doMove({ ...moveConflict.payload, override_reason: overrideReason })}
                data-testid="move-override-confirm">Override & Move</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
